import { Liquid } from "liquidjs";
import * as Sentry from "@sentry/vue";

// Create a liquid engine instance
const engine = new Liquid({
  // Configure options similar to Rails Liquid 5.5
  strictFilters: false,
  strictVariables: false,
  trimTagRight: false,
  trimTagLeft: false,
  trimOutputRight: false,
  trimOutputLeft: false,
});

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function resolveDotPath(context, path) {
  let value = context;
  for (const key of path.split(".")) {
    if (value == null || !Object.hasOwn(value, key)) return undefined;
    value = value[key];
  }
  return value;
}

function substituteSimpleTokens(template, context) {
  const roots = Object.keys(context ?? {}).map(escapeRegExp).filter(Boolean);
  if (!roots.length) return template;
  const tokenRegex = new RegExp(`{{\\s*(${roots.join("|")})\\.([\\w.\\-]+?)\\s*}}`, "g");
  return template.replace(tokenRegex, (_match, root, path) => {
    const value = resolveDotPath(context, `${root}.${path}`);
    return value == null ? "" : String(value);
  });
}

let lastReportedAt = 0;

function reportLiquidFailure(name) {
  if (Date.now() - lastReportedAt < 60000) return;
  lastReportedAt = Date.now();
  Sentry.captureMessage(`liquid parse failure: ${name}`, {
    level: "warning",
    fingerprint: ["liquid-parse-failure", name],
  });
}

/**
 * Parse a liquid template string with the provided context
 * @param {string} template - The liquid template string
 * @param {object} context - The context object containing variables
 * @returns {Promise<string>} - The rendered template
 */
export async function parseLiquidTemplate(template, context = {}) {
  try {
    if (!template || typeof template !== "string") {
      return template || "";
    }

    return await engine.parseAndRender(template, context);
  } catch (_e) {
    // Return original template if parsing fails
    return template;
  }
}

/**
 * Synchronous version of liquid template parsing
 * Note: This should be used with simple templates only
 * @param {string} template - The liquid template string
 * @param {object} context - The context object containing variables
 * @param {object} [options] - Parsing options
 * @param {boolean} [options.enabled=false] - When false, the template is returned unrendered
 * @returns {string} - The rendered template
 */
export function parseLiquidTemplateSync(
  template,
  context = {},
  { enabled = false } = { enabled: false },
) {
  try {
    if (!template || typeof template !== "string") {
      return template || "";
    }

    if (!enabled) return template;

    // Parse the template first
    const parsed = engine.parse(template);
    // Render synchronously (works for simple templates without async operations)
    return engine.renderSync(parsed, context);
  } catch (e) {
    const name = e?.name ?? "Error";
    const at = /line:(\d+), col:(\d+)/.exec(String(e?.message ?? ""));
    console.warn(`[liquidParser] parse failed: ${name}${at ? ` at line ${at[1]}, col ${at[2]}` : ""} (template length ${template.length})`);
    reportLiquidFailure(name);
    try {
      return substituteSimpleTokens(template, context);
    } catch {
      return template;
    }
  }
}

export default engine;
